<template>
  <Table.Base :auto-columns="false">
    <Table.Headers>
      <Table.Header class="tablet-h:pl-[18px]"> Your files </Table.Header>
      <Table.Header class="hidden tablet-h:flex w-1/2!">
        Location
      </Table.Header>
    </Table.Headers>

    <Table.Row
      v-for="item in props.items"
      :key="item.id"
      @click="handleItemClick(item)"
    >
      <Table.Cell class="tablet-h:pl-[18px]" :class="getCellClasses(item)">
        <ThumbnailIcon
          class="mr-2.5 min-w-9 max-w-9 h-9 tablet-h:min-w-6 tablet-h:max-w-6 tablet-h:h-6"
          :icon-size="iconSize"
          :type="getType(item)"
          :image-url="
            applyAuthorizationToken(item.file?.imageUrl, [
              {
                key: 'width',
                value: 72,
              },
            ])
          "
        />
        <div
          v-truncate
          class="w-full font-secondary text-[14px] text-gray-800 leading-[18px]"
        >
          <div class="flex items-center">
            <div data-truncate-target class="w-full whitespace-normal">
              {{ item.name }}
            </div>
            <Icon
              v-if="isLicense(item)"
              name="DacrLicense"
              :size="18"
              class="ml-2 fill-gray-800"
            />
            <div
              v-if="isCoOwner(item)"
              v-tooltip="'Co-owned copyright'"
              class="ml-2 w-[22px] h-[22px] bg-gray-800/5 rounded-sm flex items-center justify-center"
            >
              <Icon name="CoOwner" :size="14" class="stroke-gray-800/60" />
            </div>
            <div
              v-if="isAuthor(item)"
              v-tooltip="'Authored copyright'"
              class="ml-2 w-[22px] h-[22px] bg-gray-800/5 rounded-sm flex items-center justify-center"
            >
              <Icon name="Author" :size="14" class="stroke-[#7B7C7E]" />
            </div>
          </div>
          <div
            data-truncate-target
            class="tablet-h:hidden text-[13px] opacity-50 mt-0.5"
            @click.stop="handlePathClick(item.parentPath)"
          >
            {{ formatPath(item.parentPath) }}
          </div>
        </div>
      </Table.Cell>
      <Table.Cell class="hidden tablet-h:flex w-1/2!">
        <div v-truncate class="w-full font-secondary text-[14px] text-gray-800">
          <div
            v-if="item.parentPath"
            class="hover:underline"
            data-truncate-target
            @click.stop="handlePathClick(item.parentPath)"
          >
            {{ formatPath(item.parentPath) }}
          </div>
          <div v-else>- -</div>
        </div>
      </Table.Cell>
    </Table.Row>
  </Table.Base>

  <div
    v-if="items?.length"
    class="flex items-center h-[40px] pl-2.5 tablet-h:pl-[18px] mb-3"
    @click="handleClose"
  >
    <RouterLink :to="`/search/files?query=${props.query}`">
      <Link>View all results in your files</Link>
    </RouterLink>
  </div>
  <div
    v-else
    class="flex items-center h-[64px] tablet-h:h-[40px] pl-2.5 tablet-h:pl-[18px] border-b border-gray-800/8 font-secondary text-[14px] leading-[18px] text-gray-800/50 mb-3"
  >
    None of your files match your search
  </div>
</template>

<script setup lang="ts">
import { ThumbnailIcon, Table, Icon } from '@dacr/ui'
import {
  CopyrightType,
  FileAuthorRole,
  FileOwnerRole,
  FileStatus,
  ItemType,
  type Item,
} from '@/shared/graphql/legacy/generated'
import { useDevice } from '@/shared/composables/useDevice'
import { useRouter } from 'vue-router'
import { computed } from 'vue'
import { applyAuthorizationToken } from '@/shared/utils/url'
import Link from '@/shared/components/Link.vue'

const props = defineProps<{
  items?: Item[]
  query: string
}>()

const emit = defineEmits<{
  (e: 'close'): void
}>()

const device = useDevice()
const router = useRouter()

const iconSize = computed(() => (device.isMobile.value ? 36 : 24))

const isItemInactive = (item: Item) => {
  return item.file?.status === FileStatus.Inactive
}

const getCellClasses = (item: Item) => {
  return {
    ...(isItemInactive(item) && {
      'opacity-50': true,
      'pointer-events-none': true,
    }),
  }
}

const getType = (item: Item) => {
  if (item.type === ItemType.Folder) {
    return ItemType.Folder
  }

  return item.file!.type
}

const formatPath = (path: string) => {
  if (path === '/') {
    return 'Dacr'
  }

  return path
}

const isLicense = (item: Item) => {
  return item.file?.copyrightType === CopyrightType.License
}

const isCoOwner = (item: Item) => {
  return item.file?.ownershipRoles.includes(FileOwnerRole.Owner)
}

const isAuthor = (item: Item) => {
  return item.file?.authorRoles.includes(FileAuthorRole.Author)
}

const handleItemClick = (item: Item) => {
  if (item.type === ItemType.File && !isItemInactive(item)) {
    router.push(`/file/${item.file?.copyrightId}`)
  }

  if (item.type === ItemType.Folder) {
    router.push(`/home${item.parentPath}/${item.name}`.replace('//', '/'))
  }

  handleClose()
}

const handlePathClick = (path: string) => {
  if (path === '/') {
    router.push('/home')
  } else {
    router.push(`/home${path}`)
  }

  handleClose()
}

const handleClose = () => {
  emit('close')
}
</script>
